<template>
  <div class="page-container bg-gray-50">
    <!-- Main Layout -->
    <div class="main-layout md:mx-[20px] md:mt-[25px] md:mb-[25px] md:shadow-lg md:rounded-[5px] md:overflow-hidden">
      <!-- Sidebar -->
      <aside
        class="sidebar md:rounded-[5px]"
        :class="{
          'sidebar-collapsed': !isSidebarOpen,
          'sidebar-mobile-open': isSidebarOpen && isMobile,
          'sidebar-mobile-hidden': !isSidebarOpen && isMobile,
        }"
      >
        <!-- Sidebar Header -->
        <div class="sidebar-header">
          <h2 v-show="isSidebarOpen" class="sidebar-title" style="margin-left: 10px;">
            {{ embassyName }}
          </h2>
          <button
            class="sidebar-toggle"
            @click="toggleSidebar"
            :aria-label="isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'"
          >
            <i class="fas fa-bars"></i>
          </button>
        </div>

        <!-- Navigation Menu -->
        <nav class="sidebar-nav">
          <button
            v-for="item in filteredMenuItems"
            :key="item.id"
            @click="selectItem(item.id)"
            class="nav-item"
            :class="{ 'nav-item-active': selectedItem === item.id }"
          >
            <i :class="item.icon" class="nav-icon"></i>
            <span v-show="isSidebarOpen" class="nav-text">
              {{ item.text }}
            </span>
          </button>
        </nav>
      </aside>

      <!-- Main Content Area -->
      <main class="content-area">
        <!-- Mobile Menu Toggle -->
        <button
          v-if="isMobile"
          class="mobile-menu-toggle"
          @click="toggleSidebar"
          :aria-label="isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'"
        >
          <i class="fas fa-bars"></i>
        </button>

        <!-- Loading State -->
        <div v-if="isLoading" class="loading-overlay">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ $t('loading') }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-container">
            <div class="error-content-box">
                <div class="error-icon-wrapper">
                    <i class="fas fa-exclamation-triangle"></i>
                </div>
                <h3 class="error-main-title">{{ $t('dashboard.error.title') }}</h3>
                <p class="error-details-text">{{ $t('dashboard.error.suggestion') }}</p>
                <!-- <p class="error-suggestion-text">
                    {{ $t('error.suggestion') }}
                </p> -->
                <button @click="retryLoading" class="primary-retry-button">
                    <i class="fas fa-sync-alt mr-2"></i> {{ $t('dashboard.error.retry') }}
                </button>
            </div>
        </div>

        <!-- Content Views -->
        <div v-else>
            <!-- Home View (default) -->
            <section v-if="!selectedItem || selectedItem === 'home'" class="content-section">
              <div class="grid-container">
                <!-- About Section Card -->
                <div class="about-section card-style">
                  <!-- Image Container - Only shown if image loads successfully -->
                  <div
                    class="image-container relative"
                    v-if="hasImage"
                  >
                    <img
                      :src="embassyImageUrl"
                      alt="Embassy Image"
                      class="structure-image"
                      @load="handleImageLoad"
                      @error="handleImageError"
                    />
                    <div class="image-overlay"></div>
                  </div>

                  <!-- Content Section -->
                  <div class="p-6" :class="{ 'no-image-padding': !hasImage }">
                    <h2 class="structure-title">{{ embassyName }}</h2>
                    <div class="about-content">
                      <h3 class="section-title">{{ $t('about') }}</h3>
                      <hr class="divider" />
                      <p class="description-text">
                        {{ embassyDescription }}
                      </p>
                      <a
                        v-if="isValidValue(embassy.contacts?.website_url)"
                        :href="embassy.contacts.website_url"
                        class="read-more"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ $t('more') }}
                      </a>
                    </div>
                  </div>
                </div>

                <!-- Ambassador Card -->
                <div class="boss-card card-style">
                  <h3 class="boss-title">{{ ambassadorTitle }}</h3>
                  <div class="boss-content">
                    <img
                      :src="getAmbassadorPhotoUrl(ambassador?.photo)"
                      alt="Ambassador Photo"
                      class="boss-image"
                      @error="handleAmbassadorImageError"
                    />
                    <h4 class="boss-name">{{ ambassadorName }}</h4>
                    <div class="boss-social-links">
                      <div v-for="(contact, key) in filteredSocialContacts" :key="key" class="social-link-item">
                        <a
                          v-if="isValidValue(contact.value)"
                          :href="contact.url(contact.value)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="social-link"
                          @click.prevent="openExternalLinks(contact.url(contact.value))"
                        >
                          <i :class="contact.iconClass" class="social-icon"></i>
                          <span class="social-value">{{ contact.platform }}</span>
                        </a>
                        <div v-else class="social-link-disabled">
                          <i :class="contact.iconClass" class="social-icon"></i>
                          <span class="social-text" style="color: red;">{{ $t('noContactAvailable', { platform: contact.platform }) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Contact Section -->
              <div class="contact-section card-style">
                <h3 class="section-title">{{ $t('contactUs') }}</h3>
                <hr class="divider" />
                <div class="contact-grid">
                  <div class="contact-column" v-for="(column, index) in chunkedContactItems" :key="index">
                    <div
                      v-for="item in column"
                      :key="item.id"
                      @click="handleContactClick(item)"
                      :class="{ 'cursor-pointer': getDisplayLink(item) }"
                      class="contact-item-wrapper"
                    >
                      <ContactItem
                        :icon="item.icon"
                        :value="getDisplayValue(item)"
                        :link="getDisplayLink(item)"
                        :type="item.type"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Ambassador View -->
            <section v-if="selectedItem === 'ambassador'" class="content-section">
                <div class="boss-detail-card card-style">
                    <div class="boss-header">
                        <h2 class="section-header">{{ ambassadorTitle }}</h2>
                    </div>
                    <div class="boss-detail-content">
                        <div class="boss-profile">
                            <img :src="getAmbassadorPhotoUrl(ambassador?.photo)" alt="Ambassador Photo" class="boss-detail-image" @error="handleAmbassadorImageError" />
                            <h3 class="boss-detail-name">{{ ambassadorName }}</h3>
                            <p class="boss-position">{{ ambassador?.title }}</p>
                        </div>

                        <div class="boss-bio-section" v-if="ambassadorBio">
                            <h4 class="bio-title">{{ $t('dashboard.bossView.biography') }}</h4>
                            <p class="boss-bio">{{ ambassadorBio }}</p>
                        </div>

                        <div class="boss-contact-section">
                            <h4 class="contact-title">{{ $t('bossContactSection') }}</h4>
                            <div class="boss-social-grid">
                                <div v-for="(contact, key) in filteredSocialContacts" :key="key" class="social-item">
                                    <a v-if="isValidValue(contact.value)" :href="contact.url(contact.value)" target="_blank" rel="noopener noreferrer" class="social-link-detailed" @click.prevent="openExternalLinks(contact.url(contact.value))">
                                        <i :class="contact.iconClass" class="social-icon-detailed"></i>
                                        <div class="social-info">
                                            <span class="social-platform">{{ contact.platform }}:</span>
                                            <span class="social-value-detailed">{{ contact.value }}</span>
                                        </div>
                                    </a>
                                    <div v-else class="social-item-disabled">
                                        <i :class="contact.iconClass" class="social-icon-detailed"></i>
                                        <div class="social-info">
                                            <span class="social-platform">{{ contact.platform }}:</span>
                                            <span class="social-text-detailed" style="color: red;">{{ $t('noContactAvailable') }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Contact View -->
            <section v-if="selectedItem === 'contact'" class="content-section">
              <h2 class="section-header">{{ $t('contactUs') }}</h2>
              <div class="contact-detail-grid">
                 <div
                    v-for="item in contactItems"
                    :key="item.id"
                    @click="handleContactClick(item)"
                    :class="{ 'cursor-pointer': getDisplayLink(item) }"
                  >
                  <ContactItem
                    :icon="item.icon"
                    :value="getDisplayValue(item)"
                    :link="getDisplayLink(item)"
                    :type="item.type"
                    detailed
                  />
                </div>
              </div>
            </section>

            <!-- Services View -->
            <section v-if="selectedItem === 'services'" class="content-section">
                <h2 class="section-header">{{ $t('servicesOffered') }}</h2>
                <div class="mission-content">
                    <div v-if="embassy.services?.length" class="missions-grid">
                        <div v-for="(service, index) in embassy.services" :key="service.id || index" class="mission-card card-style">
                            <div class="mission-icon">
                                <i :class="getServiceIcon(index)"></i>
                            </div>
                            <div class="mission-details">
                                <h4 class="mission-title">{{ service.title || $t('serviceNumber', { number: index + 1 }) }}</h4>
                                <p class="mission-description">
                                    {{ service.description || $t('noDescriptionAvailable') }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div v-else class="no-missions">
                        <div class="coming-soon-container">
                            <i class="fas fa-hourglass-half coming-soon-icon"></i>
                            <h3 class="coming-soon-title">{{ $t('embassy.noServicesTitle') }}</h3>
                            <p class="coming-soon-text">{{ $t('embassy.noServicesDescription') }}</p>
                            <p class="coming-soon-note">{{ $t('embassy.noServicesNote') }}</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ContactItem from "@/components/common/ContactItem.vue";
import { useI18n } from 'vue-i18n';
import { useEmbassyStore } from '@/stores/embassy';

export default {
  name: "EmbassyDetailPage",
  components: {
    ContactItem
  },
  setup() {
    // --- INJECTIONS AND COMPOSABLES ---
    const { t, locale } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const embassyStore = useEmbassyStore();
    const axios = inject('api');

    // --- COMPONENT STATE REFS ---
    const isSidebarOpen = ref(false);
    const selectedItem = ref('home');
    const isMobile = ref(false);
    const isLoading = ref(true);
    const error = ref(null);
    const embassy = ref({});
    const hasImage = ref(true);

    // --- DATA SANITIZATION AND DISPLAY HELPERS ---
    const isValidValue = (val) => {
      if (val === null || val === undefined) return false;
      const s = String(val).toLowerCase().trim();
      return s !== '' && s !== 'nan' && s !== 'none';
    };

    const getFullImageUrl = (path, defaultImg) => {
      if (!isValidValue(path)) return defaultImg;
      if (path.startsWith('http')) return path;
      const baseUrl = process.env.VUE_APP_API_BASE_URL || '';
      return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    const getAmbassadorPhotoUrl = (photo) => {
      return getFullImageUrl(photo, '/assets/images/avatar.png');
    };
    
    // --- COMPUTED PROPERTIES ---
    const ambassador = computed(() => embassy.value?.ambassador || {});
    const embassyName = computed(() => isValidValue(embassy.value.name) ? embassy.value.name : t('embassy.embassyOf', { country: embassy.value.country || '...' }));
    const embassyDescription = computed(() => isValidValue(embassy.value.description) ? embassy.value.description : t('noDescriptionAvailable'));
    const ambassadorTitle = computed(() => isValidValue(ambassador.value.title) ? ambassador.value.title : t('embassy.ambassador'));
    const ambassadorName = computed(() => isValidValue(ambassador.value.name) ? ambassador.value.name : t('noInformationAvailable'));
    const ambassadorBio = computed(() => isValidValue(ambassador.value.bio) ? ambassador.value.bio : t('noDescriptionAvailable'));
    const embassyImageUrl = computed(() => getFullImageUrl(embassy.value.photo, ''));
    
    const menuItemsStructure = [
      { id: 'home', icon: 'fas fa-home', getText: () => t('home') },
      { id: 'ambassador', icon: 'fas fa-user-tie', getText: () => ambassadorTitle.value },
      { id: 'services', icon: 'fas fa-concierge-bell', getText: () => t('embassy.services') },
      { id: 'contact', icon: 'fas fa-address-book', getText: () => t('contactUs') }
    ];
    const validSectionIds = menuItemsStructure.map(item => item.id);

    const filteredMenuItems = computed(() => 
      menuItemsStructure.map(item => ({
        id: item.id,
        icon: item.icon,
        text: item.getText()
      }))
    );

    const contactItems = computed(() => [
        { id: 1, icon: 'fas fa-globe', field: 'website_url', type: 'url', value: embassy.value.contacts?.website_url, link: val => val },
        { id: 4, icon: 'far fa-envelope', field: 'email', type: 'email', value: embassy.value.contacts?.email, link: val => `mailto:${val}` },
        { id: 5, icon: 'fas fa-phone', field: 'phone', type: 'tel', value: embassy.value.contacts?.phone, link: val => `tel:${val}` },
        { id: 2, icon: 'fab fa-facebook-f', field: 'facebook_url', type: 'url', value: embassy.value.contacts?.facebook_url, link: val => val },
        { id: 3, icon: 'fab fa-twitter', field: 'twitter_url', type: 'url', value: embassy.value.contacts?.twitter_url, link: val => val },
        { id: 8, icon: 'fab fa-youtube', field: 'youtube_url', type: 'url', value: embassy.value.contacts?.youtube_url, link: val => val },
        // These are now handled correctly by the simplified getDisplayLink
        { id: 6, icon: 'fas fa-map-marker-alt', field: 'location', type: 'location', value: embassy.value.contacts?.location },
       // { id: 7, icon: 'fas fa-directions', field: 'google_map_directions', type: 'location', value: embassy.value.contacts?.google_map_directions },
    ].filter(item => isValidValue(item.value)));
    
    const getDisplayValue = (item) => isValidValue(item.value) ? item.value : t(`no${item.field}Available`);
    
    // ===================================================================
    // === THE FIX FOR THE UNCLICKABLE LOCATION IS HERE ===
    // This logic is now simple and reliable.
    // ===================================================================
    const getDisplayLink = (item) => {
        if (!isValidValue(item.value)) {
            return null;
        }
        // For location types, the value itself is the destination (address or URL)
        if (item.type === 'location') {
            return item.value;
        }
        // For other types, generate the link using the link function
        if (typeof item.link === 'function') {
            return item.link(item.value);
        }
        return null; // Fallback
    };

    const chunkedContactItems = computed(() => {
      const validContacts = contactItems.value;
      const chunkSize = Math.ceil(validContacts.length / 2) || 1;
      return [validContacts.slice(0, chunkSize), validContacts.slice(chunkSize)];
    });

    const filteredSocialContacts = computed(() => {
        const socialMap = {
            facebook_url: { platform: 'Facebook', iconClass: 'fab fa-facebook-f', url: val => val },
            twitter_url: { platform: 'Twitter', iconClass: 'fab fa-twitter', url: val => val },
            email: { platform: 'Email', iconClass: 'far fa-envelope', url: val => `mailto:${val}` },
        };
        return Object.keys(socialMap).map(key => ({
            ...socialMap[key],
            value: ambassador.value?.contacts?.[key]
        }));
    });

    const fetchEmbassyDetails = async () => {
      isLoading.value = true;
      error.value = null;
      hasImage.value = true;
      const countryName = route.params.name;

      if (!countryName) {
        error.value = t('embassy.error.invalidEmbassy');
        isLoading.value = false;
        return;
      }
      
      try {
        let embassyFromStore = embassyStore.findEmbassyByCountry(countryName);
        if (!embassyFromStore) {
          await embassyStore.fetchEmbassies(locale.value);
          embassyFromStore = embassyStore.findEmbassyByCountry(countryName);
        }
        if (!embassyFromStore) {
          throw new Error(t('embassy.error.embassyNotFound'));
        }
        if (embassyFromStore.isFullyLoaded) {
          embassy.value = embassyFromStore;
          isLoading.value = false;
          return;
        }

        embassy.value = embassyFromStore;
        
        const embassyId = embassyFromStore.id;
        const ambassadorId = embassyFromStore.ambassador?.id;
        
        const promises = [
          axios.get(`/embassy_retrieve_contacts/?embassyId=${embassyId}`),
          axios.get(`/embassy_retrieve_services/?embassyId=${embassyId}`)
        ];
        if (ambassadorId) {
          promises.push(axios.get(`/ambassador_retrieve_contacts/?ambassadorId=${ambassadorId}`));
        } else {
          promises.push(Promise.resolve(null));
        }

        const [contactsRes, servicesRes, ambassadorContactsRes] = await Promise.all(promises);

        const fullEmbassyData = {
          ...embassyFromStore,
          contacts: contactsRes.data?.[0] || {},
          services: servicesRes.data || [],
          ambassador: {
            ...embassyFromStore.ambassador,
            contacts: ambassadorContactsRes?.data?.[0] || {}
          },
          isFullyLoaded: true,
        };
        
        embassy.value = fullEmbassyData;
        embassyStore.updateEmbassyDetails(fullEmbassyData);
      } catch (err) {
          error.value = err.message || t('embassy.error.fetchingData');
      } finally {
          isLoading.value = false;
      }
    };

    // --- METHODS AND LIFECYCLE HOOKS ---
    const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };
    const checkScreenSize = () => { isMobile.value = window.innerWidth <= 768; isSidebarOpen.value = !isMobile.value; };
    const handleImageLoad = () => { hasImage.value = true; };
    const handleImageError = () => { hasImage.value = false; };
    const handleAmbassadorImageError = (event) => { event.target.src = '/assets/images/avatar.png'; };
    const retryLoading = () => fetchEmbassyDetails();
    const selectItem = (itemId) => {
      if (selectedItem.value !== itemId) {
        selectedItem.value = itemId;
        router.replace({ query: { section: itemId } });
      }
      if (isMobile.value) isSidebarOpen.value = false;
    };
    const openExternalLinks = (url) => { if (isValidValue(url)) window.open(url, '_blank', 'noopener,noreferrer'); };

    const openDirectionsInGoogleMaps = (destination) => {
      if (!("geolocation" in navigator)) {
        const fallbackUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destination)}`;
        openExternalLinks(fallbackUrl);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const origin = `${latitude},${longitude}`;
          const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${encodeURIComponent(destination)}`;
          openExternalLinks(directionsUrl);
        },
        () => {
          const fallbackUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destination)}`;
          openExternalLinks(fallbackUrl);
        }
      );
    };
    
    // This handler now works correctly with the fixed getDisplayLink function.
    const handleContactClick = (item) => {
      const linkValue = getDisplayLink(item);
      if (!linkValue) return;

      if (item.type === 'location') {
        openDirectionsInGoogleMaps(linkValue);
      } else {
        openExternalLinks(linkValue);
      }
    };
    
    const getServiceIcon = (index) => ['fas fa-passport', 'fas fa-file-alt', 'fas fa-id-card', 'fas fa-handshake'][index % 4];

    onMounted(() => {
      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);
      fetchEmbassyDetails();
    });

    onBeforeUnmount(() => { window.removeEventListener('resize', checkScreenSize); });

    watch(() => route.params.name, (newName, oldName) => { if (newName && newName !== oldName) fetchEmbassyDetails(); });
    
    watch(() => route.query.section, (newSection) => {
        selectedItem.value = validSectionIds.includes(newSection) ? newSection : 'home';
    }, { immediate: true });

    // Re-fetch embassy data when UI language changes
    watch(
      () => locale.value,
      async (newLang, oldLang) => {
        if (newLang === oldLang) return;
        // Clear the isFullyLoaded flag so fetchEmbassyDetails doesn't return stale cached data
        if (embassy.value?.id) {
          embassy.value = { ...embassy.value, isFullyLoaded: false };
          // Also clear in the store
          const idx = embassyStore.embassies.findIndex(e => e.id === embassy.value.id);
          if (idx !== -1) {
            embassyStore.embassies[idx] = { ...embassyStore.embassies[idx], isFullyLoaded: false };
          }
        }
        // Clear the store's embassy list so fetchEmbassies re-fetches with new language
        embassyStore.$patch({ embassies: [] });
        isLoading.value = true;
        try {
          await fetchEmbassyDetails();
        } finally {
          isLoading.value = false;
        }
      }
    );

    return {
      isSidebarOpen, selectedItem, isMobile, isLoading, error, embassy, ambassador,
      filteredMenuItems, filteredSocialContacts, contactItems, embassyImageUrl, hasImage,
      embassyName, embassyDescription, ambassadorTitle, ambassadorName, ambassadorBio,
      toggleSidebar, selectItem, handleImageError, handleAmbassadorImageError, handleImageLoad,
      getAmbassadorPhotoUrl,
      getServiceIcon, openExternalLinks, retryLoading, fetchEmbassyDetails,
      isValidValue, getDisplayValue, getDisplayLink, handleContactClick, chunkedContactItems,
    };
  }
};
</script>

<style scoped>
/* Copied styles from StructureDisplayPage */
.page-container {
  @apply min-h-screen flex flex-col;
}

.main-layout {
  @apply flex flex-col md:flex-row flex-1;
}

.mobile-menu-toggle {
  @apply self-start p-2 bg-[#144A78] text-white rounded-md shadow-lg mb-4 hover:bg-blue-800 transition-colors;
  @apply md:hidden;
}

.sidebar {
  @apply bg-[#144A78] text-white flex flex-col transition-all duration-300 ease-in-out;
  @apply w-full md:w-64 fixed md:relative h-full md:h-auto z-40;
  @apply transform -translate-x-full md:translate-x-0;
  margin: 0;
}

.sidebar-mobile-open { @apply translate-x-0; }
.sidebar-mobile-hidden { @apply -translate-x-full; }
.sidebar-collapsed { @apply md:w-20; }
.sidebar-header { @apply flex items-center justify-between mb-8 p-4; }
.sidebar-title { @apply text-xl font-bold truncate; }
.sidebar-toggle { @apply p-2 rounded-md hover:bg-blue-700 transition-colors md:block hidden; }
.sidebar-nav { @apply space-y-2 px-4 flex-1; }
.nav-item { @apply flex items-center py-2.5 px-3 rounded-lg transition-all duration-200 w-full text-left hover:bg-blue-700; }
.nav-item-active { @apply bg-blue-700; }
.nav-icon { @apply min-w-[24px] text-center text-lg; }
.nav-text { @apply ml-3 font-bold whitespace-normal truncate; }
.sidebar-footer { @apply mt-auto p-4; }
.contact-card { @apply bg-white rounded-lg p-3 text-blue-900; }
.contact-avatar { @apply flex items-center mb-2; }
.avatar-image { @apply w-8 h-8 object-cover rounded-full border-2 border-blue-200; }
.contact-name { @apply ml-2 font-medium; }
.contact-email { @apply text-sm break-all; }

.content-area { @apply flex flex-col flex-1 p-4 md:p-6 bg-white; position: relative; }
.content-section { @apply mb-8; }
.card-style { @apply bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300; }
.card-style:hover { @apply shadow-xl transform -translate-y-1; }
.grid-container { @apply grid grid-cols-1 lg:grid-cols-3 gap-6; }
.about-section { @apply lg:col-span-2; }
.image-container { @apply relative overflow-hidden; }
.structure-image { @apply w-full h-48 md:h-64 object-cover; }
.image-overlay { @apply absolute inset-0 bg-gradient-to-t from-black/50 to-transparent; }
.structure-title { @apply text-2xl md:text-3xl font-bold mb-4 text-blue-900 text-left; }
.about-content { @apply text-left; }
.section-title { @apply text-lg md:text-xl font-bold my-2 text-blue-900; }
.divider { @apply border-t border-blue-200 my-4; }
.description-text { @apply text-gray-700 mb-4 text-base md:text-lg leading-relaxed; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; }
.read-more { @apply text-blue-600 hover:underline font-semibold; }

.boss-card { @apply bg-[#144A78] text-white p-6; }
.boss-title { @apply text-lg font-bold mb-4 border-b border-blue-500 pb-2; }
.boss-content { @apply bg-white w-full flex flex-col items-center p-4 rounded-lg; }
.boss-image { @apply w-24 h-24 rounded-full mb-3 object-cover border-4 border-white shadow-md; }
.boss-name { @apply text-lg font-semibold mb-2 text-black; }
.boss-social-links { @apply w-full grid grid-cols-2 gap-3 mt-4; }
.social-link-item { @apply w-full; }
.social-link { @apply flex items-center p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors; }
.social-link-disabled { @apply flex items-center p-2 rounded-lg bg-gray-100 text-gray-500; }
.social-icon { @apply w-5 h-5 flex-shrink-0 text-blue-600; }
.social-value { @apply ml-2 text-sm font-medium text-gray-700; }
.social-text { @apply ml-2 text-sm text-gray-500; }

.contact-section { @apply mt-8 p-6; }
.contact-grid { @apply grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-4; }
.contact-column { @apply space-y-2; }
.contact-item-wrapper { @apply rounded-lg transition-colors duration-200; }
.contact-item-wrapper:has(a) { @apply hover:bg-gray-100; }

.boss-detail-card { @apply bg-white; }
.boss-header { @apply bg-[#144A78] text-white p-6; }
.boss-detail-content { @apply p-6; }
.boss-profile { @apply flex flex-col items-center text-center mb-8 mt-3; }
.boss-detail-image { @apply w-32 h-32 rounded-full mb-4 object-cover border-4 border-white shadow-lg -mt-20; }
.boss-detail-name { @apply text-2xl font-bold mb-1 text-gray-800; }
.boss-position { @apply text-lg text-blue-600 font-medium mb-4; }
.boss-bio-section { @apply mb-8; }
.bio-title { @apply text-xl font-bold mb-4 text-gray-800 border-b pb-2; }
.boss-bio { @apply text-gray-700 leading-relaxed; }
.boss-contact-section { @apply mt-8; }
.contact-title { @apply text-xl font-bold mb-4 text-gray-800 border-b pb-2; }
.boss-social-grid { @apply grid grid-cols-1 md:grid-cols-2 gap-4; }
.social-item { @apply w-full; }
.social-link-detailed { @apply flex items-center p-4 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors; }
.social-item-disabled { @apply flex items-center p-4 rounded-lg bg-gray-100 text-gray-500; }
.social-icon-detailed { @apply w-6 h-6 flex-shrink-0 text-blue-600; }
.social-info { @apply ml-4 truncate; display: flex; flex-direction: column; align-items: flex-start; }
.social-platform { @apply text-sm font-medium text-gray-600; text-align: left; width: 100%; }
.social-value-detailed { @apply text-sm text-gray-700 mt-1; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; width: 100%; text-align: left; }
.social-text-detailed { @apply text-sm text-gray-500 mt-1; }
.contact-detail-grid { @apply grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6; }
.section-header { @apply text-2xl md:text-3xl font-bold mb-6 text-gray-800; }

.missions-grid { @apply grid gap-6 mt-6; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); }
.mission-card { @apply flex items-start p-4; }
.mission-icon { @apply mr-4 p-3 bg-blue-50 rounded-full text-blue-600; }
.mission-icon i { @apply text-xl; }
.mission-details { @apply flex-1; }
.mission-title { @apply text-lg font-semibold text-gray-800 mb-1; }
.mission-description { @apply text-gray-600 text-sm leading-relaxed; }

.no-missions, .no-regionals { @apply p-8 text-center; }
.coming-soon-container { @apply max-w-2xl mx-auto bg-blue-50 bg-opacity-50 p-8 rounded-xl border border-blue-100; }
.coming-soon-icon { @apply text-4xl text-blue-400 mb-4; }
.coming-soon-title { @apply text-xl font-semibold text-blue-800 mb-3; }
.coming-soon-text { @apply text-gray-700 mb-4 leading-relaxed; }
.coming-soon-note { @apply text-sm text-gray-500 italic; }

.loading-overlay { @apply fixed inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center z-50; }
.loading-spinner { @apply animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-800; }
.loading-text { @apply mt-4 text-gray-600; }

.error-container { @apply flex items-center justify-center h-full w-full p-4; }
.error-content-box { @apply bg-white p-8 rounded-lg shadow-xl text-center max-w-lg mx-auto border border-red-200; }
.error-icon-wrapper { @apply text-red-500 text-5xl mb-4; }
.error-main-title { @apply text-2xl font-bold text-gray-800 mb-2; }
.error-details-text { @apply text-gray-600 mb-4; }
.error-suggestion-text { @apply text-sm text-gray-500 mb-6; }
.primary-retry-button { @apply bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 transition-transform transform hover:scale-105; }

@media (max-width: 768px) {
  .sidebar, .sidebar-collapsed { @apply w-full; }
  .nav-text { @apply ml-2; }
  .grid-container, .contact-grid, .boss-social-grid, .contact-detail-grid { @apply grid-cols-1; }
  .sidebar-footer { @apply hidden; }
  .boss-card, .contact-section { @apply mt-6; }
  .mission-card { @apply flex-col items-center text-center; }
  .mission-icon { @apply mr-0 mb-3; }
  .boss-detail-image { @apply w-24 h-24 -mt-16; }
}

.truncate { @apply overflow-hidden text-ellipsis whitespace-nowrap; }
.break-words { word-break: break-word; }
.cursor-pointer { cursor: pointer; }
.about-section {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.image-container {
  height: 250px;
  width: 100%;
}

.structure-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%);
}

.no-image-padding {
  padding-top: 2rem;
}

.structure-title {
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: var(--primary-color);
}

.section-title {
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: var(--text-secondary);
}

.divider {
  border: none;
  height: 1px;
  background-color: var(--border-color);
  margin: 1rem 0;
}

.description-text {
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.read-more {
  display: inline-block;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
}

.read-more:hover {
  text-decoration: underline;
  transform: translateX(4px);
}

@media (max-width: 768px) {
  .image-container {
    height: 200px;
  }
}
</style>